import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { plansService } from '../services/plansService';

const AuthContext = createContext({});

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userOrg, setUserOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [bypassMode, setBypassMode] = useState(false);

  // ---- Mock user for bypass mode ----
  const createBypassUser = () => ({
    id: 'bypass-user-001',
    email: 'bypass@temp.local',
    created_at: new Date()?.toISOString(),
    aud: 'authenticated',
    role: 'authenticated',
    app_metadata: { role: 'admin' },
    user_metadata: { name: 'Bypass User' }
  });

  // ---- Load profile from public.users ----
  const loadProfile = async (userId) => {
    if (!userId || bypassMode) {
      // In bypass mode, create a mock profile
      if (bypassMode) {
        setUserProfile({
          id: 'bypass-user-001',
          email: 'bypass@temp.local',
          full_name: 'Bypass User',
          role: 'admin',
          org_id: 'bypass-org-001',
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        });
        setUserOrg({
          id: 'bypass-org-001',
          name: 'Bypass Organization',
          plan: { name: 'Enterprise', features: [] }
        });
      }
      return;
    }

    setProfileLoading(true);
    try {
      const { data: profile, error } = await supabase?.from('users')?.select('*')?.eq('id', userId)?.single();

      if (error) throw error;
      setUserProfile(profile);

      if (profile?.org_id) {
        const { data: orgData, error: orgError } =
          await plansService?.getOrgWithPlan(profile?.org_id);
        if (!orgError && orgData) setUserOrg(orgData);
      }

      setConnectionError(null);
    } catch (err) {
      console.error('Profile load error:', err);
      setConnectionError('Unable to load user profile. Please try again.');
      setUserProfile(null);
      setUserOrg(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const clearProfile = () => {
    setUserProfile(null);
    setUserOrg(null);
    setProfileLoading(false);
  };

  // ---- Retry connection function (now implemented) ----
  const retryConnection = async () => {
    try {
      setConnectionError(null);
      setLoading(true);

      // Simple connection test without problematic health endpoints
      const { data, error } = await supabase?.auth?.getSession();
      
      if (error && error?.status >= 500) {
        console.warn('Auth service still unavailable:', error);
        return false;
      }
      
      // Connection successful
      const session = data?.session ?? null;
      setUser(session?.user ?? null);
      if (session?.user?.id) {
        await loadProfile(session?.user?.id);
      }
      
      return true;
    } catch (err) {
      console.error('Retry connection failed:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ---- Bypass mode toggle ----
  const enableBypassMode = () => {
    console.log('🚨 ENABLING BYPASS MODE - TEMPORARY ACCESS GRANTED');
    setBypassMode(true);
    setConnectionError(null);
    const mockUser = createBypassUser();
    setUser(mockUser);
    loadProfile(mockUser?.id);
    setLoading(false);
  };

  const disableBypassMode = () => {
    console.log('🔒 DISABLING BYPASS MODE - RETURNING TO NORMAL AUTH');
    setBypassMode(false);
    setUser(null);
    clearProfile();
    setLoading(true);
    // Reinitialize auth
    init();
  };

  // ---- Initialize session and listener ----
  const init = async () => {
    try {
      const { data, error } = await supabase?.auth?.getSession();
      
      if (error) {
        console.error('Auth init error:', error);
        if (error?.status >= 500) {
          setConnectionError(
            'Authentication service temporarily unavailable. You can use bypass mode for immediate access.'
          );
        } else {
          setConnectionError('Authentication service error. Please try again.');
        }
        setLoading(false);
        return;
      }

      const session = data?.session ?? null;
      setUser(session?.user ?? null);
      if (session?.user?.id) await loadProfile(session?.user?.id);

      setConnectionError(null);
    } catch (err) {
      console.error('Auth init exception:', err);
      setConnectionError(
        'Network error while connecting to authentication service. You can use bypass mode for immediate access.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let unsub;

    if (bypassMode) {
      setLoading(false);
      return;
    }

    init();

    // Subscribe to auth state changes (skip in bypass mode)
    if (!bypassMode) {
      try {
        const sub = supabase?.auth?.onAuthStateChange((_event, session) => {
          if (bypassMode) return; // Ignore auth changes in bypass mode
          
          const nextUser = session?.user ?? null;
          setUser(nextUser);
          if (nextUser?.id) loadProfile(nextUser?.id);
          else clearProfile();
        });
        unsub = sub?.data?.subscription?.unsubscribe ?? sub?.subscription?.unsubscribe;
      } catch (err) {
        console.error('Auth listener setup error:', err);
      }
    }

    return () => {
      if (typeof unsub === 'function') unsub();
    };
  }, [bypassMode]);

  // ---- Auth actions ----
  const signIn = async (email, password) => {
    if (bypassMode) {
      // In bypass mode, any credential works
      const mockUser = createBypassUser();
      setUser(mockUser);
      await loadProfile(mockUser?.id);
      return { data: { user: mockUser }, error: null };
    }

    try {
      setConnectionError(null);
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email: (email || '')?.trim()?.toLowerCase(),
        password: (password || '')?.trim(),
      });

      if (error) {
        if (error?.status >= 500) {
          setConnectionError(
            'Authentication service temporarily unavailable. You can use bypass mode for immediate access.'
          );
        }
        return { data, error };
      }

      if (data?.user) {
        setUser(data?.user);
        if (data?.user?.id) await loadProfile(data?.user?.id);
      }

      return { data, error: null };
    } catch (err) {
      console.error('Sign-in error:', err);
      setConnectionError('Network error contacting authentication service. You can use bypass mode for immediate access.');
      return { data: null, error: { message: 'Network error. Try again or use bypass mode.' } };
    }
  };

  const signUp = async (email, password, userData = {}) => {
    if (bypassMode) {
      return { data: null, error: { message: 'Signup not available in bypass mode.' } };
    }

    try {
      const { data, error } = await supabase?.auth?.signUp({
        email: (email || '')?.trim()?.toLowerCase(),
        password: (password || '')?.trim(),
        options: { data: userData },
      });
      return { data, error };
    } catch (err) {
      console.error('Sign-up error:', err);
      return { data: null, error: { message: 'Network error. Try again.' } };
    }
  };

  const signOut = async () => {
    if (bypassMode) {
      disableBypassMode();
      return { error: null };
    }

    try {
      const { error } = await supabase?.auth?.signOut();
      if (!error) {
        setUser(null);
        clearProfile();
      }
      return { error };
    } catch (err) {
      console.error('Sign-out error:', err);
      return { error: { message: 'Network error. Try again.' } };
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return { data: null, error: { message: 'No user logged in.' } };
    try {
      const { data, error } = await supabase?.from('users')?.update(updates)?.eq('id', user?.id)?.select()?.single();
      if (!error) setUserProfile(data);
      return { data, error };
    } catch (err) {
      console.error('Profile update error:', err);
      return { data: null, error: { message: 'Network error. Try again.' } };
    }
  };

  const handleSignupSuccess = async (planCode) => {
    if (!user || !userProfile?.org_id)
      return { error: { message: 'User or organization not found.' } };
    try {
      const { error } = await plansService?.handleSuccessfulSignup(
        userProfile?.org_id,
        planCode
      );
      if (!error) loadProfile(user?.id);
      return { error };
    } catch (err) {
      return {
        error: { message: 'Failed to assign plan. Please contact support.' },
      };
    }
  };

  const value = {
    user,
    userProfile,
    userOrg,
    loading,
    profileLoading,
    connectionError,
    bypassMode,
    signIn,
    signUp,
    signOut,
    updateProfile,
    handleSignupSuccess,
    retryConnection,
    enableBypassMode,
    disableBypassMode,
    isAuthenticated: !!user,
  };

  return (<AuthContext.Provider value={value}>{children}</AuthContext.Provider>);
};